"use client";

import { ChevronRight, Pin, PinOff } from "lucide-react";
import { Link } from "react-router-dom";
import type { FocusEvent } from "react";

import { IconButton, Tooltip } from "@/components/ui-system";
import { navItemActive, navItemOrChildActive, type SidebarNavGroup } from "@/lib/navigation/sidebar-nav";
import { cn } from "@/lib/utils/cn";

export type WorkspacePrimarySidebarProps = {
  currentPath: string;
  groups: SidebarNavGroup[];
  expanded: boolean;
  pinned: boolean;
  onNavigate: () => void;
  onTogglePin: () => void;
  onFocusContainer: () => void;
  onBlurContainer: (event: FocusEvent<HTMLDivElement>) => void;
  companyName?: string | null;
};

function initials(value: string): string {
  const parts = value.trim().split(/\s+/).filter(Boolean);
  return (parts.slice(0, 2).map((part) => part[0]?.toUpperCase() ?? "").join("") || "AT").slice(0, 2);
}

export function WorkspacePrimarySidebar({
  currentPath,
  groups,
  expanded,
  pinned,
  onNavigate,
  onTogglePin,
  onFocusContainer,
  onBlurContainer,
  companyName,
}: WorkspacePrimarySidebarProps) {
  const brandName = companyName?.trim() || "Atech Energy";
  return (
    <div className="flex min-h-0 flex-1 flex-col outline-none" onFocusCapture={onFocusContainer} onBlurCapture={onBlurContainer}>
      <div className={cn("flex h-[var(--topbar-height)] shrink-0 items-center border-b border-card-border", expanded ? "gap-ds-3 pl-[calc((var(--sidebar-width-collapsed)-2.25rem)/2)] pr-ds-3" : "justify-center px-ds-2")}>
        <span className="workspace-sidebar-logo flex h-9 w-9 shrink-0 items-center justify-center rounded-control bg-accent font-bold tracking-tight text-accent-foreground shadow-raised" aria-hidden>{initials(brandName)}</span>
        {expanded ? <div className="min-w-0 flex-1"><p title={brandName} className="min-w-0 truncate text-micro uppercase tracking-[0.14em] text-muted">{brandName}</p><p className="truncate text-caption font-semibold">Operations workspace</p></div> : null}
        {expanded ? <IconButton variant="ghost" className="workspace-sidebar-action" aria-label={pinned ? "Unpin sidebar" : "Pin sidebar open"} title={pinned ? "Unpin sidebar" : "Pin sidebar open"} aria-pressed={pinned} onClick={onTogglePin}>{pinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}</IconButton> : null}
      </div>

      <nav className={cn("scrollbar-hidden flex-1 overflow-y-auto py-ds-3", expanded ? "px-0" : "px-ds-3")} aria-label="Workspace sections">
        {groups.map((group, groupIndex) => (
          <section key={group.id} className={cn(groupIndex > 0 && "mt-ds-4")} aria-labelledby={expanded ? `nav-group-${group.id}` : undefined}>
            <h2
              id={expanded ? `nav-group-${group.id}` : undefined}
              aria-hidden={!expanded}
              className={cn(
                "relative mb-ds-1 px-ds-2 text-micro font-semibold uppercase tracking-[0.12em]",
                expanded
                  ? "text-muted"
                  : "text-transparent after:absolute after:left-[calc((var(--sidebar-width-collapsed)-2.5rem)/2)] after:right-[calc((var(--sidebar-width-collapsed)-2.5rem)/2)] after:top-1/2 after:h-px after:bg-card-border",
              )}
            >
              {group.label}
            </h2>
            <ul className="space-y-ds-1">
              {group.items.map((item) => {
                const active = navItemOrChildActive(currentPath, item);
                const Icon = item.icon;
                const link = (
                  <Link
                    to={item.href}
                    onClick={onNavigate}
                    aria-current={active ? "page" : undefined}
                    aria-label={item.label}
                    className={cn(
                      "workspace-sidebar-link group/nav flex min-h-10 items-center rounded-control border border-transparent transition-colors duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30",
                      expanded
                        ? "ml-[calc((var(--sidebar-width-collapsed)-2.5rem)/2)] mr-ds-2 gap-ds-3 pl-[calc((2.5rem-1.125rem)/2)] pr-ds-3"
                        : "w-10 justify-center px-0",
                      active ? "workspace-sidebar-link-active border-accent/20 bg-accent-muted text-accent" : "text-muted hover:bg-surface-hover hover:text-foreground",
                    )}
                  >
                    <Icon className="h-[1.125rem] w-[1.125rem] shrink-0" aria-hidden />
                    {expanded ? <><span className="min-w-0 flex-1 truncate text-caption font-medium">{item.label}</span>{item.children?.length ? <ChevronRight className="h-3.5 w-3.5 text-muted" aria-hidden /> : null}</> : null}
                  </Link>
                );
                return (
                  <li key={item.href}>
                    {expanded ? link : <Tooltip content={item.label} placement="right-start" wrapperClassName="flex w-full justify-center" delay={100}>{link}</Tooltip>}
                    {expanded && active && item.children?.length ? (
                      <ul className="ml-[1.35rem] mt-ds-1 space-y-ds-1 border-l border-card-border pl-ds-3">
                        {item.children.map((child) => {
                          const childActive = navItemActive(currentPath, child.href);
                          return <li key={child.href}><Link to={child.href} onClick={onNavigate} aria-current={childActive ? "page" : undefined} className={cn("workspace-sidebar-link flex min-h-8 items-center rounded-control border border-transparent px-ds-2 text-micro transition-colors", childActive ? "workspace-sidebar-link-active bg-accent-muted font-semibold text-accent" : "text-muted hover:bg-surface-hover hover:text-foreground")}><span className="truncate">{child.label}</span></Link></li>;
                        })}
                      </ul>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </nav>
    </div>
  );
}

export function WorkspaceMobileNavigation({ currentPath, groups, onNavigate }: { currentPath: string; groups: SidebarNavGroup[]; onNavigate: () => void }) {
  return (
    <div className="workspace-mobile-navigation space-y-ds-6">
      {groups.map((group) => (
        <section key={group.id}>
          <h2 className="mb-ds-2 px-ds-2 text-micro font-semibold uppercase tracking-[0.12em] text-muted">{group.label}</h2>
          <ul className="space-y-ds-1">
            {group.items.map((item) => {
              const active = navItemOrChildActive(currentPath, item);
              const Icon = item.icon;
              return (
                <li key={item.href}>
                  <Link to={item.href} onClick={onNavigate} aria-current={active ? "page" : undefined} className={cn("workspace-sidebar-link flex min-h-11 items-center gap-ds-3 rounded-control border border-transparent px-ds-3 text-caption font-medium", active ? "workspace-sidebar-link-active bg-accent-muted text-accent" : "text-foreground hover:bg-surface-hover")}><Icon className="h-5 w-5 shrink-0" /><span className="min-w-0 flex-1 truncate">{item.label}</span></Link>
                  {active && item.children?.length ? <ul className="ml-ds-6 border-l border-card-border pl-ds-3">{item.children.map((child) => { const childActive = navItemActive(currentPath, child.href); return <li key={child.href}><Link to={child.href} onClick={onNavigate} className={cn("workspace-sidebar-link flex min-h-9 items-center rounded-control border border-transparent px-ds-2 text-micro", childActive ? "workspace-sidebar-link-active bg-accent-muted font-semibold text-accent" : "text-muted hover:bg-surface-hover hover:text-foreground")}>{child.label}</Link></li>; })}</ul> : null}
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}
